import axios from "axios";
import { API_URL } from "../../constants";
import { authHeader, listingAuthHeader, setFunnelToken } from "../../utils/auth";
import {
  captureIntakeFailure,
  parseIntakeFailure,
  retryableIntakeFailure,
} from "../../utils/intakeLimits";
import {
  IMPORT_STATUSES,
  bindZillowImportListing,
  prepareZillowImportSession,
  requireManualZillowImportRetry,
} from "../../utils/zillowImportSession";
import {
  SUBMIT_LISTING,
  SUBMIT_LISTING_FAIL,
  SUBMIT_LISTING_SUCCESS,
  UPDATE_LISTING_IMPORT,
} from "../actionTypes";

export const IMPORT_HTTP_TIMEOUT_MS = 15000;

/** @param {any} error */
const importFailure = (error, remember = true) => ({
  error:
    (remember ? captureIntakeFailure(error) : parseIntakeFailure(error)) ||
    error?.response?.data ||
    error?.message ||
    "We could not reach the import service.",
  status: error?.response?.status ?? null,
});

/**
 * Save the request identity and capability before making the first POST.
 * @param {{ zillow_url: string }} listingData
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {(dispatch: import('redux').Dispatch, getState?: () => any) => Promise<any>}
 */
export const submitListing =
  (listingData, { signal } = {}) =>
  async (dispatch, getState) => {
    let session;
    try {
      session = prepareZillowImportSession(listingData.zillow_url);
    } catch (error) {
      const failure = importFailure(error);
      dispatch({ type: SUBMIT_LISTING_FAIL, payload: failure });
      return { ok: false, ...failure };
    }
    const meta = { importRequestId: session.requestId };
    dispatch({
      type: SUBMIT_LISTING,
      payload: { importRequestId: session.requestId, listingUuid: session.listingUuid },
    });
    try {
      const response = await axios.post(
        `${API_URL}/listings/import-zillow/`,
        { zillow_url: session.sourceUrl, import_request_id: session.requestId },
        {
          headers: { ...authHeader(), "X-Listino-Funnel-Token": session.funnelToken },
          timeout: IMPORT_HTTP_TIMEOUT_MS,
          signal,
        }
      );
      if (
        signal?.aborted ||
        (getState && getState().listings.importRequestId !== session.requestId)
      ) {
        dispatch({ type: SUBMIT_LISTING_FAIL, meta, payload: { error: null, status: null } });
        return { ok: false, aborted: true, session };
      }
      const data = response.data;
      if (
        !data ||
        typeof data.uuid !== "string" ||
        !data.uuid ||
        !data.import ||
        !IMPORT_STATUSES.includes(data.import.status)
      ) {
        throw new Error("The import response was incomplete. Resume to check the same request.");
      }
      const bound = bindZillowImportListing(session.requestId, data.uuid);
      if (!bound) {
        dispatch({ type: SUBMIT_LISTING_FAIL, meta, payload: { error: null, status: null } });
        return { ok: false, aborted: true, session };
      }
      setFunnelToken(data.uuid, session.funnelToken);
      dispatch({ type: SUBMIT_LISTING_SUCCESS, meta, payload: data });
      return { ok: true, data, session: bound };
    } catch (error) {
      const failure = signal?.aborted ? { error: null, status: null } : importFailure(error);
      if (!signal?.aborted && retryableIntakeFailure(failure.error, failure.status)) {
        session = requireManualZillowImportRetry(session.requestId) || session;
      }
      dispatch({ type: SUBMIT_LISTING_FAIL, meta, payload: failure });
      return { ok: false, ...failure, aborted: !!signal?.aborted, session };
    }
  };

/**
 * Poll and retry/cancel the existing import without claiming or replacing its listing.
 * @param {string} listingUuid
 * @param {"status" | "retry" | "cancel"} operation
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {(dispatch: import('redux').Dispatch) => Promise<any>}
 */
export const requestListingImport =
  (listingUuid, operation, { signal } = {}) =>
  async (dispatch) => {
    const url = `${API_URL}/listings/${listingUuid}/import-${operation}/`;
    const config = {
      headers: listingAuthHeader(listingUuid),
      timeout: IMPORT_HTTP_TIMEOUT_MS,
      signal,
    };
    try {
      const response =
        operation === "status" ? await axios.get(url, config) : await axios.post(url, {}, config);
      if (signal?.aborted) return { ok: false, aborted: true };
      const data = response.data;
      if (
        data?.uuid !== listingUuid ||
        !data.import ||
        !IMPORT_STATUSES.includes(data.import.status)
      ) {
        throw new Error("The import status was incomplete. Check progress again.");
      }
      dispatch({ type: UPDATE_LISTING_IMPORT, payload: data, meta: { listingUuid } });
      return { ok: true, data };
    } catch (error) {
      return {
        ok: false,
        ...importFailure(error, operation === "retry"),
        aborted: !!signal?.aborted,
      };
    }
  };
